import {
    Building2,
    Skull,
    Fingerprint,
    BarChart3,
    GraduationCap,
    Shield,
    type LucideIcon,
} from 'lucide-react';
import type { DemoCategory } from '../../data/demoGraphs';

export type DashboardIconTone =
    | 'live-wallets'
    | 'threats'
    | 'crime-patterns'
    | 'analytics'
    | 'tutorials'
    | 'neutral'
    | 'success'
    | 'info'
    | 'accent'
    | 'warning'
    | 'destructive'
    | 'primary';

export type DashboardIconVariant = 'tile' | 'section' | 'inline' | 'stat';
export type DashboardIconSize = 'sm' | 'md' | 'lg';

export const DASHBOARD_ICON_SIZES: Record<DashboardIconSize, string> = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
};

export const VARIANT_DEFAULT_SIZE: Record<DashboardIconVariant, DashboardIconSize> = {
    tile: 'md',
    section: 'md',
    inline: 'sm',
    stat: 'sm',
};

export const TONE_ICON_CLASSES: Record<DashboardIconTone, string> = {
    'live-wallets': 'text-primary',
    threats: 'text-destructive',
    'crime-patterns': 'text-amber-600 dark:text-amber-400',
    analytics: 'text-primary',
    tutorials: 'text-muted-foreground',
    neutral: 'text-muted-foreground',
    success: 'text-green-600 dark:text-green-400',
    info: 'text-muted-foreground',
    accent: 'text-primary',
    warning: 'text-amber-600 dark:text-amber-400',
    destructive: 'text-destructive',
    primary: 'text-primary',
};

export type CategoryIconConfig = {
    icon: LucideIcon;
    tone: DashboardIconTone;
    label: string;
    description: string;
};

export const CATEGORY_ICON_MAP: Record<DemoCategory, CategoryIconConfig> = {
    'live-wallets': {
        icon: Building2,
        tone: 'live-wallets',
        label: 'Live Wallets',
        description: 'Real exchange & notable wallet addresses',
    },
    threats: {
        icon: Skull,
        tone: 'threats',
        label: 'Threats & Compliance',
        description: 'Sanctions, scams, and high-risk addresses',
    },
    'crime-patterns': {
        icon: Fingerprint,
        tone: 'crime-patterns',
        label: 'Crime Patterns',
        description: 'ML/TF typology demonstrations',
    },
    analytics: {
        icon: BarChart3,
        tone: 'analytics',
        label: 'Advanced Analytics',
        description: 'Chainalysis-level analysis features',
    },
    tutorials: {
        icon: GraduationCap,
        tone: 'tutorials',
        label: 'Investigation Tutorials',
        description: 'How-to guides and workflow demos',
    },
};

export const ALL_CATEGORY_CONFIG: CategoryIconConfig = {
    icon: Shield,
    tone: 'neutral',
    label: 'All',
    description: 'All demo scenarios',
};

/** Strip leading emoji / flag sequences from display strings. */
export function stripEmoji(text: string): string {
    return text
        .replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '')
        .replace(/[\p{Extended_Pictographic}\uFE0F]/gu, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

export function getCategoryConfig(category: DemoCategory): CategoryIconConfig {
    return CATEGORY_ICON_MAP[category];
}

export function categoryToTone(category: DemoCategory): DashboardIconTone {
    return CATEGORY_ICON_MAP[category].tone;
}
